/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
import Cookies from 'js-cookie';
import consoleApi from './console';

import {
  handleResponseError,
  removeEmpty,
  formatLimitsResponse,
  formatLimitResponse,
  formatLimitQuery,
} from '../utils';

const currentTeam = parseInt(Cookies.get('sila_console_team'), 10);

const fetchConsoleUsers = async (options, signal) => {
  const limit = options.limit || 20;
  const page = options.page || 1;
  const query = options.query || '';
  try {
    const response = await consoleApi.get({
      url: `/superuser/console_users?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getConsoleUser = async (handle) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/console_users/${handle}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateConsoleUser = async (handle, values) => {
  if (values.first_name && values.last_name) {
    values.full_name = `${values.first_name} ${values.last_name}`;
  }
  try {
    const response = await consoleApi.put({
      url: `/superuser/console_users/${handle}`,
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchEndUsers = async (options, signal) => {
  const limit = options.limit || 20;
  const page = options.page || 1;
  const { query } = options;
  try {
    const response = await consoleApi.get({
      url: `/superuser/end_users?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getEndUser = async (handle) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/end_users/${handle}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateEndUser = async (handle, values) => {
  if (values.first_name && values.last_name) {
    values.full_name = `${values.first_name} ${values.last_name}`;
  }
  try {
    const response = await consoleApi.put({
      url: `/superuser/end_users/${handle}`,
      data: values,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getPriorityMigrationStatus = async (handle) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/end_user/priority_migration/${handle}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const migrateToPriority = async (handle, includeWalletsWithBalance, piUuids) => {
  const response = await consoleApi.post({
    url: `/superuser/end_user/priority_migration/${handle}`,
    data: {
      include_wallets_with_balance: includeWalletsWithBalance,
      pi_uuids: piUuids,
    },
  });
  return response;
};

const getEndUserDocuments = async (handle) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/end_users/${handle}/documents`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getDocumentTypes = async () => {
  try {
    const response = await consoleApi.get({
      url: '/document_types',
      data: {
        per_page: 100,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getEndUserDocument = async (handle, id) => {
  try {
    const response = await consoleApi.get({
      binary: true,
      notracking: true,
      url: `/superuser/end_users/${handle}/documents/${id}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const getVerificationStatuses = async () => {
  try {
    const response = await consoleApi.get({
      url: '/verification_statuses',
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getKycLevels = async () => {
  try {
    const response = await consoleApi.get({
      url: '/kyc_levels',
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchTransactions = async (options, signal) => {
  const defaultSort = '&sort_keys=created_desc';
  const limit = options.limit || 20;
  const page = options.page || 1;
  const query = options.query.includes('created')
    ? options.query
    : `${options.query}${defaultSort}`;
  try {
    const response = await consoleApi.get({
      url: `/superuser/transactions?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getTransaction = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/transactions/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getTransactionGraph = async (id) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/transactions/${id}/graph`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const getTransactionTransfers = async (id) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/transactions/${id}/transfers`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const getWalletBalance = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/get_wallet_balance/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchApps = async (options, signal) => {
  const defaultSort = '&sort_keys=created_desc';
  const limit = options.limit || 20;
  const page = options.page || 1;
  const query = options.query.includes('created')
    ? options.query
    : `${options.query}${defaultSort}`;
  try {
    const response = await consoleApi.get({
      url: `/superuser/apps?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAppSalesTier = async (appHandle, values) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/apps/${appHandle}`,
      data: {
        sales_tier_id: values.id,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAppTerminationDate = async (appHandle, date) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/apps/${appHandle}`,
      data: {
        termination_date: date,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getApplication = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/apps/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchSalesTiers = async (options) => {
  try {
    const response = await consoleApi.get({
      url: '/sales_tiers',
      data: {
        limit: options.limit || 20,
        page: options.page || 1,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getEndUserPaymentInstruments = async (options) => {
  const { handle } = options;
  const limit = options.limit || 50;
  const page = options.page || 1;
  const query = options.query || '';
  const team = options.team || currentTeam;
  try {
    const response = await consoleApi.get({
      url: `/superuser/payment_instruments?handle=${handle}&team_id=${team}&limit=${limit}&page=${page}${query}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const paymentInstrumentFreezeUnfreeze = async (
  payment_instrument_id,
  team_id,
  force_freeze,
  freeze_reasons
) => {
  try {
    const response = await consoleApi.put({
      url: '/superuser/payment_instruments',
      data: removeEmpty({
        payment_instrument_id: payment_instrument_id,
        team_id: team_id,
        force_freeze: force_freeze,
        freeze_reasons: freeze_reasons,
      }),
    });
    if (!response.success && !(response.status_code === 400 || response.status_code === 409)) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getPaymentInstrument = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/payment_instrument/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAdminRequestReturn = async (uuid, values) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/action_reverse_virtual_account_ach_transaction/${uuid}`,
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAdminRequestReturnSubmitted = async (values) => {
  try {
    const response = await consoleApi.post({
      url: '/superuser/reverse_virtual_account_ach_transaction',
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const setVaccountPrefix = async (values) => {
  try {
    const response = await consoleApi.post({
      url: '/superuser/provision_vaccount_prefix',
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const setVaccountLimit = async (values) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/apps/${values.app_id}/app_settings`,
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getVaccountPrefix = async () => {
  try {
    const response = await consoleApi.get({
      url: '/superuser/get_available_vaccount_prefix',
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const createInstantSettlement = async (id, values) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/instant_settlement/${id}`,
      data: removeEmpty(values),
    });

    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateInstantSettlement = async (id, values) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/instant_settlement/${id}`,
      data: removeEmpty(values),
    });

    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const createCompanyWallet = async (id, values) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/apps/${id}/setup_company_wallet`,
      data: removeEmpty(values),
    });

    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const saveStatement = async (values) => {
  try {
    const response = await consoleApi.post({
      url: '/superuser/business_statement_information',
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateStatement = async (id, values) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/business_statement_information/${id}`,
      data: removeEmpty(values),
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteStatementLogo = async (id, team = currentTeam) => {
  try {
    const response = await consoleApi.delete({
      url: `/superuser/${id}/logo_delete`,
      data: {
        team_id: team,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchCompanyWallet = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/apps/${id}/get_company_wallets`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const getCkoCredentials = async (id) => {
  try {
    const response = await consoleApi.post({
      url: '/superuser/cko_credentials',
      data: {
        app_id: id,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateCkoCredentials = async (id, values, method) => {
  if (method === 'post') values = removeEmpty(values);
  try {
    const response = await consoleApi[method]({
      url: '/superuser/cko_credentials',
      data: {
        app_id: id,
        ...values,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

export const getProviderWebhooks = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/provider_webhooks/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateProviderWebhooks = async (id, values, method) => {
  values = removeEmpty(values);
  try {
    const response = await consoleApi[method]({
      url: '/superuser/provider_webhook',
      data: {
        app_id: id,
        ...values,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteProviderWebhooks = async (id, values) => {
  try {
    const response = await consoleApi.delete({
      url: '/superuser/provider_webhook',
      data: {
        app_id: id,
        ...values,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const userFreezeUnfreeze = async (freeze_reasons, handle) => {
  try {
    const response = await consoleApi.put({
      url: '/superuser/freeze_user',
      data: {
        freeze_reasons: freeze_reasons,
        handle: handle,
      },
    });
    if (!response.success && !(response.status_code === 400 || response.status_code === 409)) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const cancelACHTransactions = async (values) => {
  try {
    const response = await consoleApi.post({
      url: '/superuser/cancel_transaction',
      data: values,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const appFreezeUnfreeze = async (appId, freeze_reasons) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/apps/${appId}`,
      data: {
        freeze_reasons: freeze_reasons,
      },
    });
    // 400 indicates an app cant be unfrozen for the given reason
    // 409 indicates an app is not frozen, so cant be unfroze
    if (!response.success && !(response.status_code === 400 || response.status_code === 409)) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchLimits = async (options, signal) => {
  const defaultSort = '&sort_keys=begins_at_desc';
  const limit = options.limit || 20;
  const page = options.page || 1;
  const query = options.query.includes('sort_keys')
    ? options.query
    : `${options.query}${defaultSort}`;
  try {
    const response = await consoleApi.get({
      url: `/superuser/limits?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    formatLimitsResponse(response);
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchLimit = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/limits/${id}`,
    });
    if (!response.success || response.status_code !== 200) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const updateLimit = async (id, data, initialValues) => {
  formatLimitQuery(data, initialValues);
  try {
    const response = await consoleApi.put({
      url: `/superuser/limits/${id}`,
      data: data,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteLimit = async (id) => {
  try {
    const response = await consoleApi.delete({
      url: `/superuser/limits/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

const createLimit = async (data) => {
  const requestData = {};
  Object.entries(data).forEach(([key, val]) => {
    if (val !== '') requestData[key] = val;
  });
  formatLimitQuery(requestData, requestData);

  try {
    const response = await consoleApi.post({
      url: '/superuser/limits',
      data: requestData,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchLimitEnums = async () => {
  try {
    const urls = {
      transactionGraphLabel: '/superuser/enums/transaction_graph_labels?selector=limit',
      limitType: '/superuser/enums/limit_type',
      userCategory: '/superuser/enums/user_category',
      providerNames: '/superuser/enums/provider_names?selector=limit',
    };

    const requests = Object.entries(urls).map(([key, url]) =>
      consoleApi.get({ url }).then((response) => {
        if (!response.success) {
          throw handleResponseError(response);
        }
        return { [key]: response.enum };
      })
    );

    const detailsResponse = await Promise.all(requests);

    const result = detailsResponse.reduce((acc, obj) => ({ ...acc, ...obj }), { success: true });

    return result;
  } catch (error) {
    throw error;
  }
};

const fetchLimitOverrides = async (options, signal) => {
  const defaultSort = '&sort_keys=begins_at_desc';
  const limit = options.limit || 20;
  const page = options.page || 1;
  const query = options.query.includes('sort_keys')
    ? options.query
    : `${options.query}${defaultSort}`;

  try {
    const response = await consoleApi.get({
      url: `/superuser/limit_overrides?limit=${limit}&page=${page}${query}`,
      signal,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    formatLimitsResponse(response);
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchLimitOverride = async (id) => {
  try {
    const response = await consoleApi.get({
      url: `/superuser/limit_overrides/${id}`,
    });
    if (!response.success || response.status_code !== 200) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const updateLimitOverride = async (id, data, initialValues) => {
  formatLimitQuery(data, initialValues);
  try {
    const response = await consoleApi.put({
      url: `/superuser/limit_overrides/${id}`,
      data: data,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteLimitOverride = async (id) => {
  try {
    const response = await consoleApi.delete({
      url: `/superuser/limit_overrides/${id}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

const createLimitOverride = async (data) => {
  const responseData = {};
  Object.entries(data).forEach(([key, val]) => {
    if (val !== '') responseData[key] = val;
  });
  formatLimitQuery(responseData);

  try {
    const response = await consoleApi.post({
      url: `/superuser/limit_overrides`,
      data: responseData,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }

    formatLimitResponse(response.limit);
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchKycFlows = async (appHandle) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/kyc_flows/${appHandle}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchKYCFlowOptions = async (appHandle) => {
  try {
    const response = await consoleApi.post({
      url: `/superuser/kyc_options/${appHandle}`,
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const evaluateKYCFlow = async (uuid, approved) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/kyc_flow/${uuid}`,
      data: {
        approved: approved,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const updateAppFeature = async (appId, app_features) => {
  try {
    const response = await consoleApi.put({
      url: `/superuser/apps/${appId}`,
      data: {
        update_feature: app_features,
      },
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchAppFeatures = async () => {
  try {
    const response = await consoleApi.get({
      url: '/superuser/enums/app_feature?selector=enableable',
    });
    if (!response.success) {
      throw handleResponseError(response);
    }
    return response.enum;
  } catch (error) {
    throw error;
  }
};

const adminApi = {
  fetchConsoleUsers,
  getConsoleUser,
  updateConsoleUser,
  fetchEndUsers,
  getEndUser,
  updateEndUser,
  getPriorityMigrationStatus,
  migrateToPriority,
  getEndUserDocuments,
  getDocumentTypes,
  getEndUserDocument,
  getVerificationStatuses,
  getKycLevels,
  fetchTransactions,
  getTransaction,
  getTransactionGraph,
  getTransactionTransfers,
  getWalletBalance,
  fetchApps,
  updateAppSalesTier,
  updateAppTerminationDate,
  getApplication,
  fetchSalesTiers,
  getEndUserPaymentInstruments,
  paymentInstrumentFreezeUnfreeze,
  updateAdminRequestReturn,
  updateAdminRequestReturnSubmitted,
  setVaccountPrefix,
  setVaccountLimit,
  getVaccountPrefix,
  createInstantSettlement,
  updateInstantSettlement,
  createCompanyWallet,
  getPaymentInstrument,
  saveStatement,
  updateStatement,
  deleteStatementLogo,
  getCkoCredentials,
  updateCkoCredentials,
  getProviderWebhooks,
  updateProviderWebhooks,
  deleteProviderWebhooks,
  fetchCompanyWallet,
  userFreezeUnfreeze,
  cancelACHTransactions,
  appFreezeUnfreeze,
  fetchLimit,
  updateLimit,
  deleteLimit,
  createLimit,
  fetchLimits,
  fetchLimitEnums,
  fetchLimitOverrides,
  fetchLimitOverride,
  updateLimitOverride,
  deleteLimitOverride,
  createLimitOverride,
  evaluateKYCFlow,
  fetchKycFlows,
  fetchKYCFlowOptions,
  updateAppFeature,
  fetchAppFeatures,
};

export default adminApi;
